/**
 * ルートプラン一覧の画面
 *
 * 地図と常設シートは MapShell が持つ。地図に重ねるのはこれから行くプランの経路だけで、
 * 終わったプランはシート末尾に畳んでおき、開いたときだけ経路を取りに行って線を出す。
 * どのカードがどの線かは色で対応させる。
 */

import { ChevronDown, History, Plus, Route, Users } from 'lucide-react'
import { type RefObject, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import {
  createRoutePlanTrip,
  deleteRoutePlan,
  deleteRoutePlans,
  duplicateRoutePlan,
  getRoutePlanRoutes,
  getRoutePlans,
} from '@/api/services/route-plan'
import type { RoutePlanRoute, RoutePlanSummary } from '@/api/types/route-plan'
import { GoogleCircleMarker } from '@/components/shared/map/google-circle-marker'
import { RoutePolyline } from '@/components/shared/map/route-polyline'
import { SheetNavHeader } from '@/components/shared/sheet-nav-header'
import type { BottomSheetRef } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import type { ActionSection } from '@/components/ui/custom-dropdown'
import { Skeleton } from '@/components/ui/skeleton'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { apiErrorMessage } from '@/lib/api-error'
import { confirmDialog } from '@/lib/confirm-dialog'
import { cn } from '@/lib/utils'
import type { MapScreen, SheetSnap } from '@/pages/map/types'
import { PATHS } from '@/routes/paths'
import { useHiddenRoutePlansStore } from '@/store/useHiddenRoutePlansStore'

import { PlanCard } from './components/plan-card'
import { PlanListToolbar } from './components/plan-list-toolbar'
import { routePath } from './detail/components/plan-route-path'
import { PlanShareSheet } from './detail/components/plan-share-sheet'
import { isPastPlan, planColor } from './lib'

/** 日付未設定は末尾へ回し、日付ありは近い順 */
const sortPlans = (plans: RoutePlanSummary[]): RoutePlanSummary[] =>
  [...plans].sort((a, b) => {
    if (a.plan_date === b.plan_date) return b.id - a.id
    if (!a.plan_date) return 1
    if (!b.plan_date) return -1
    return a.plan_date.localeCompare(b.plan_date)
  })

const originOf = (plan: RoutePlanSummary): google.maps.LatLngLiteral | null =>
  plan.origin_lat === null || plan.origin_lon === null ? null : { lat: plan.origin_lat, lng: plan.origin_lon }

const destOf = (plan: RoutePlanSummary): google.maps.LatLngLiteral | null =>
  plan.dest_lat === null || plan.dest_lon === null ? null : { lat: plan.dest_lat, lng: plan.dest_lon }

/** 一覧に載る経路。スポットの座標に出発地・到着地を足して線にする */
const pathOf = (plan: RoutePlanSummary): google.maps.LatLngLiteral[] =>
  routePath(
    (plan.route_points ?? []).map(([lat, lng]) => ({ lat, lng })),
    originOf(plan),
    plan.return_to_origin,
    destOf(plan),
  )

/** 溜まったプランを一度に描かないための 1 ページ分。地図に出す線もここで抑える */
const PAGE_SIZE = 30

const usePaged = <T,>(items: T[]) => {
  const [limit, setLimit] = useState(PAGE_SIZE)
  const visible = useMemo(() => items.slice(0, limit), [items, limit])
  return { visible, rest: items.length - visible.length, showMore: () => setLimit((current) => current + PAGE_SIZE) }
}

type Args = {
  /** false なら別画面が地図を使っている。取得も副作用も止める */
  active: boolean
  sheetRef: RefObject<BottomSheetRef | null>
  snap: SheetSnap
}

export const usePlanListScreen = ({ active, sheetRef, snap }: Args): MapScreen => {
  const navigate = useNavigate()
  const [plans, setPlans] = useState<RoutePlanSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [tripPlanId, setTripPlanId] = useState<number | null>(null)
  const [duplicatingPlanId, setDuplicatingPlanId] = useState<number | null>(null)
  const { run: runDuplicate } = useSubmitLock()
  const [deletingPlanId, setDeletingPlanId] = useState<number | null>(null)
  const { run: runDelete } = useSubmitLock()
  const [showPast, setShowPast] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  // 行のメニューからは 1 件、ツールバーからは選んだ分。後者だけ保存後に選択を外す
  const [shareTarget, setShareTarget] = useState<{ planIds: number[]; fromSelection: boolean } | null>(null)
  const { isSubmitting: isBulkDeleting, run: runBulkDelete } = useSubmitLock()
  const [pastRoutes, setPastRoutes] = useState<Map<number, RoutePlanRoute>>(new Map())
  const hiddenIds = useHiddenRoutePlansStore((s) => s.hiddenIds)
  const toggleHidden = useHiddenRoutePlansStore((s) => s.toggle)
  const setHidden = useHiddenRoutePlansStore((s) => s.setHidden)

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setPlans(sortPlans(await getRoutePlans(signal)))
    } catch (error) {
      if (signal?.aborted) return
      toast.error(error instanceof Error ? error.message : 'ルートプランの取得に失敗しました')
    } finally {
      if (!signal?.aborted) setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!active) return
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [active, load])

  const upcomingPlans = useMemo(() => plans.filter((plan) => !isPastPlan(plan)), [plans])
  // 終わったものは直近を上に。近い順に並んでいるので反転させる
  const pastPlans = useMemo(() => plans.filter(isPastPlan).reverse(), [plans])
  const ownPlans = useMemo(() => upcomingPlans.filter((plan) => plan.is_owner), [upcomingPlans])
  const sharedPlans = useMemo(() => upcomingPlans.filter((plan) => !plan.is_owner), [upcomingPlans])

  const own = usePaged(ownPlans)
  const shared = usePaged(sharedPlans)
  const past = usePaged(pastPlans)

  // 終わったプランの経路は一覧に入っていないので、畳みを開いたときだけ取りに行く
  useEffect(() => {
    if (!showPast) return
    const missing = past.visible.filter((plan) => !pastRoutes.has(plan.id)).map((plan) => plan.id)
    if (missing.length === 0) return
    const controller = new AbortController()
    const fetchRoutes = async () => {
      try {
        const routes = await getRoutePlanRoutes(missing, controller.signal)
        setPastRoutes((current) => new Map([...current, ...routes.map((route) => [route.id, route] as const)]))
      } catch (error) {
        if (controller.signal.aborted) return
        toast.error(error instanceof Error ? error.message : '経路の取得に失敗しました')
      }
    }
    void fetchRoutes()
    return () => controller.abort()
  }, [showPast, past.visible, pastRoutes])

  // 一覧に出ているもののうち、目のマークで隠したものを除く前の候補
  const listedPlans = useMemo(() => {
    const upcoming = [...own.visible, ...shared.visible]
    if (!showPast) return upcoming
    const withRoute = (plan: RoutePlanSummary): RoutePlanSummary => {
      const route = pastRoutes.get(plan.id)
      return route ? { ...plan, route_points: route.route_points, route_polyline: route.route_polyline } : plan
    }
    return [...upcoming, ...past.visible.map(withRoute)]
  }, [showPast, own.visible, shared.visible, past.visible, pastRoutes])

  const mapPlans = useMemo(() => listedPlans.filter((plan) => !hiddenIds.includes(plan.id)), [listedPlans, hiddenIds])

  const paths = useMemo(() => mapPlans.map((plan) => ({ plan, path: pathOf(plan) })), [mapPlans])

  const fitPoints = useMemo(() => paths.flatMap((entry) => entry.path), [paths])

  const openTrip = (tripId: number) => navigate(PATHS.TRIPS.DETAIL.replace(':trip_id', String(tripId)))

  const openPlan = (planId: number) => {
    navigate(PATHS.ROUTE_PLANS.DETAIL.replace(':plan_id', String(planId)))
    sheetRef.current?.snapTo(snap.half)
  }

  const handleTrip = async (plan: RoutePlanSummary) => {
    // 旅が既にあれば作らずに開くだけ
    if (plan.trip_id !== null) {
      openTrip(plan.trip_id)
      return
    }
    setTripPlanId(plan.id)
    try {
      const trip = await createRoutePlanTrip(plan.id)
      setPlans((current) => current.map((row) => (row.id === plan.id ? { ...row, trip_id: trip.trip_id } : row)))
      toast.success(`「${trip.trip_title}」を旅の記録に追加しました`)
      openTrip(trip.trip_id)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '旅の記録の作成に失敗しました')
    } finally {
      setTripPlanId(null)
    }
  }

  const menuSections: ActionSection[] = [
    {
      type: 'action',
      label: '移動',
      items: [{ key: 'rallies', label: 'スタンプラリー一覧', onClick: () => navigate(PATHS.RALLIES.LIST) }],
    },
  ]

  const handleDuplicate = (plan: RoutePlanSummary) =>
    runDuplicate(async () => {
      setDuplicatingPlanId(plan.id)
      try {
        const copy = await duplicateRoutePlan(plan.id)
        toast.success(`「${copy.title}」を作成しました`)
        openPlan(copy.id)
      } catch (error) {
        toast.error(apiErrorMessage(error, '複製に失敗しました'))
      } finally {
        setDuplicatingPlanId(null)
      }
    })

  const handleDelete = (plan: RoutePlanSummary) =>
    runDelete(async () => {
      if (!(await confirmDialog({ title: `「${plan.title}」を削除しますか？`, destructive: true }))) return
      setDeletingPlanId(plan.id)
      try {
        await deleteRoutePlan(plan.id)
        setPlans((current) => current.filter((row) => row.id !== plan.id))
        toast.success('プランを削除しました')
      } catch (error) {
        toast.error(apiErrorMessage(error, '削除に失敗しました'))
      } finally {
        setDeletingPlanId(null)
      }
    })

  const renderMore = (paged: { rest: number; showMore: () => void }) =>
    paged.rest > 0 && (
      <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground" onClick={paged.showMore}>
        もっと見る（残り {paged.rest} 件）
      </Button>
    )

  const toggleSelected = (planId: number) =>
    setSelectedIds((current) =>
      current.includes(planId) ? current.filter((id) => id !== planId) : [...current, planId],
    )

  // 畳んだ「終わったプラン」に残った選択は操作の対象にしない
  const listedSelected = listedPlans.filter((plan) => selectedIds.includes(plan.id))
  const listedSelectedIds = listedSelected.map((plan) => plan.id)
  // 共有されたプランも地図の表示切り替えには選べる。削除・共有は自分の分だけに効かせる
  const ownSelectedIds = listedSelected.filter((plan) => plan.is_owner).map((plan) => plan.id)

  const clearSelection = () => setSelectedIds([])

  const handleBulkDelete = () =>
    runBulkDelete(async () => {
      const skipped = listedSelectedIds.length - ownSelectedIds.length
      const confirmed = await confirmDialog({
        title: `${ownSelectedIds.length} 件のプランを削除しますか？`,
        description: skipped > 0 ? `共有されたプラン ${skipped} 件は削除されません` : undefined,
        destructive: true,
      })
      if (!confirmed) return
      try {
        const deleted = await deleteRoutePlans(ownSelectedIds)
        setPlans((current) => current.filter((row) => !deleted.includes(row.id)))
        toast.success(`${deleted.length} 件のプランを削除しました`)
        clearSelection()
      } catch (error) {
        toast.error(apiErrorMessage(error, '削除に失敗しました'))
      }
    })

  const renderCard = (plan: RoutePlanSummary) => (
    <PlanCard
      key={plan.id}
      plan={plan}
      onClick={() => openPlan(plan.id)}
      isSelected={selectedIds.includes(plan.id)}
      onToggleSelect={() => toggleSelected(plan.id)}
      onTrip={() => void handleTrip(plan)}
      isCreatingTrip={tripPlanId === plan.id}
      onDuplicate={() => void handleDuplicate(plan)}
      onShare={() => setShareTarget({ planIds: [plan.id], fromSelection: false })}
      isDuplicating={duplicatingPlanId === plan.id}
      onDelete={() => void handleDelete(plan)}
      isDeleting={deletingPlanId === plan.id}
      isShownOnMap={!hiddenIds.includes(plan.id)}
      onToggleMap={() => toggleHidden(plan.id)}
    />
  )

  const listedIds = listedPlans.map((plan) => plan.id)
  const allSelected = listedIds.length > 0 && listedSelectedIds.length === listedIds.length

  return {
    title: 'ルートプラン',
    menuSections,
    mapLayer: (
      <>
        {paths.map(({ plan, path }) => (
          <RoutePolyline
            key={plan.id}
            encoded={plan.route_polyline}
            straightPath={path}
            strokeColor={planColor(plan.id)}
            strokeWeight={6}
            // 何本も重なるので、地形が透けるくらいまで落とす
            strokeOpacity={0.55}
            onClick={() => openPlan(plan.id)}
          />
        ))}

        {paths.map(({ plan }) => {
          const start = originOf(plan)
          if (!start) return null
          return (
            <GoogleCircleMarker
              key={`origin-${plan.id}`}
              position={start}
              radius={5}
              color="#FFFFFF"
              fillColor={planColor(plan.id)}
              weight={2}
              zIndex={5}
            />
          )
        })}
      </>
    ),
    fitPoints,
    fitKey: `plans-${paths.length}-${fitPoints.length}`,
    sheetHeader: (
      <>
        <SheetNavHeader
          title={<span className="tabular-nums text-muted-foreground">{upcomingPlans.length} 件のプラン</span>}
          action={
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 px-3!"
              onClick={() => navigate(PATHS.ROUTE_PLANS.CREATE)}
            >
              <Plus size={15} />
              作成
            </Button>
          }
        />
        {listedPlans.length > 0 && (
          <PlanListToolbar
            listedCount={listedPlans.length}
            mapCount={mapPlans.length}
            selectedCount={listedSelectedIds.length}
            ownSelectedCount={ownSelectedIds.length}
            isBusy={isBulkDeleting}
            onToggleAll={() => setSelectedIds(allSelected ? [] : listedIds)}
            onShow={() => setHidden(listedSelectedIds, false)}
            onHide={() => setHidden(listedSelectedIds, true)}
            onShare={() => setShareTarget({ planIds: ownSelectedIds, fromSelection: true })}
            onDelete={() => void handleBulkDelete()}
          />
        )}
      </>
    ),
    sheetBody: (
      <div className="pb-3">
        {isLoading && plans.length === 0 ? (
          <div className="space-y-3 p-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : plans.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
            <Route className="size-10" />
            <p className="text-sm">まだルートプランがありません</p>
            <p className="text-xs">「作成」から作れます</p>
          </div>
        ) : (
          <>
            {upcomingPlans.length === 0 && (
              <p className="py-10 text-center text-sm text-muted-foreground">これから行くプランはありません</p>
            )}

            {own.visible.map(renderCard)}
            {renderMore(own)}

            {sharedPlans.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-3 pt-5 pb-1.5 text-xs text-muted-foreground">
                  <Users size={13} />
                  共有されたプラン
                </div>
                {shared.visible.map(renderCard)}
                {renderMore(shared)}
              </>
            )}

            {pastPlans.length > 0 && (
              <div className="pt-3">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-xs text-muted-foreground hover:bg-muted/50"
                  onClick={() => setShowPast((current) => !current)}
                >
                  <History size={13} />
                  終わったプラン
                  <span className="tabular-nums">{pastPlans.length}</span>
                  <ChevronDown size={14} className={cn('ml-auto transition-transform', showPast && 'rotate-180')} />
                </button>
                {showPast && (
                  <div className="border-t">
                    {past.visible.map(renderCard)}
                    {renderMore(past)}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    ),
    dialogs: (
      <PlanShareSheet
        planIds={shareTarget?.planIds ?? []}
        open={shareTarget !== null}
        onOpenChange={(open) => !open && setShareTarget(null)}
        onSaved={shareTarget?.fromSelection ? clearSelection : undefined}
      />
    ),
  }
}
